import classNames from 'classnames';
import React, {
    ReactElement,
    useState,
    useEffect,
    Dispatch,
    SetStateAction,
} from 'react';

import styles from './TextInput.module.css';
import { textInputDisplayName } from './TextInput.qa';

export interface ITextInputProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    error?: string;
    hint?: string;
    value?: string;
    placeholder?: string;
    label: string;
    'data-testid'?: string;
    className?: string;
    type?: string;
    name?: string;
}

export function OzTextInput (props: ITextInputProps): ReactElement {
    const {
        error,
        hint,
        disabled = false,
        onChange,
        onBlur,
        value = '',
        placeholder = '',
        label = '',
        'data-testid': dataTest = '',
        className = '',
        type = 'text',
        name = '',
    }: ITextInputProps = props;

    const [isFocused, setIsFocused]: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(false);
    const [inputValue, setInputValue]: [string, Dispatch<SetStateAction<string>>] = useState<string>(value || '');

    useEffect((): void => {
        setInputValue(value);
    }, [value]);

    const wrapperClassName: string = classNames({
        'w-full h-7 flex flex-col justify-center items-center': true,
        'relative border rounded bg-white text-black-75': true,
        '!text-secondary': isFocused,
        '!text-error ': (error && !disabled),
        '!text-black-30': disabled,
        [className]: !!className,
    });

    const inputClassName: string = classNames({
        'w-full h-5 bg-transparent type-body1 text-secondary-contrast': true,
        'font-primary leading-normal mt-0 outline-none caret-primary px-2': true,
        [styles.OzTextInput]: true, // applies OzTextInput::placeholder styles
        'mt-2': (isFocused || !!inputValue),
        '!text-black-30': disabled,
        [styles.OzTextInputDisabled]: disabled, // applies OzTextInputDisabled::placeholder styles
        'text-secondary-contrast caret-error': (!!error && !disabled),
        [styles.OzTextInputError]: (!!error && !disabled), // applies OzTextInputDisabled::placeholder styles
    });

    const labelClassName: string = classNames({
        'w-full top-1/2 block type-subtitle2 text-left text-black-75': true,
        'font-primary !font-normal capitalize truncate opacity-100 leading-4': true,
        'absolute transition-opacity ease-in-out duration-500 px-2': true,
        '!text-secondary': isFocused,
        '!text-black-30': disabled,
        '!text-error-variant': (!!error && !disabled),
        'flex opacity-00': (!isFocused && !inputValue),
    });

    const errorClassName: string = classNames({
        'w-full type-caption font-primary font-medium italic truncate leading-12 ml-3': true,
        'text-error-variant': !!error,
    });

    const hintClassName: string = classNames({
        'w-full type-caption font-primary font-medium italic truncate leading-12 ml-3': true,
        'text-blue-300': !!hint,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setInputValue(event.target.value);
        onChange(event);
    };

    const handleBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    };

    const handleFocus = (): void => {
        setIsFocused(true);
    };

    let placeholderValue: string = placeholder;

    if (isFocused) {
        placeholderValue = '';
    }

    return (
        <div className='h-auto' data-testid={dataTest}>
            <div className={wrapperClassName}>
                <label className={labelClassName}>{label}</label>
                <input
                    name={name}
                    type={type}
                    className={inputClassName}
                    disabled={disabled}
                    placeholder={placeholderValue}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={inputValue}
                />
            </div>
            {hint && !error && isFocused &&
                <p className={hintClassName}>{hint}</p>}
            {error &&
                <p className={errorClassName}>{error}</p>}
        </div>
    );
}

OzTextInput.displayName = textInputDisplayName;
